import { ref } from 'vue'
import type { Ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'

// Stores
import { useDeviceStore } from '@/stores/DeviceStore'
import { useDataStore } from '@/stores/DataStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'

// Helpers
import { useTimeout } from '@/utils/helpers'

// Types
import type { BrowserTypes, DeviceInfo, ManagedTimeout } from '@/customTypes'

// Services
import Logger from '@/services/Logger.js'

const logger: any = new Logger('MediaDevices.ts', true)

export const useMediaDevicesStore = defineStore('mediaDevices', () => {
  const deviceStore = useDeviceStore()
  const dataStore = useDataStore()
  const activeScreenerStore = useActiveScreenerStore()
  const { setManagedTimeout, clearManagedTimeout } = useTimeout()

  const mediaDevicesApi: MediaDevices = navigator.mediaDevices
  const INPUT_DEVICE_ID_KEY: string = 'inputDeviceId'
  const OUTPUT_DEVICE_ID_KEY: string = 'outputDeviceId'

  const isIpad: boolean = deviceStore.deviceIs('IPAD')
  const isSafari: boolean = deviceStore.browserIs('SAFARI')
  const requiresAudioDevicePrompt: Ref<boolean | null> = ref(null)
  const requiresAudioDevicePromptForced: Ref<boolean> = ref(true)

  const { BrowserTypesMap } = storeToRefs(deviceStore)
  const browserTypes: Ref<BrowserTypes> = BrowserTypesMap

  const audioDevices: Ref<DeviceInfo[]> = ref([])
  const deviceChangeTimeoutId = ref<ManagedTimeout | null>(null)
  const mediaDevicesLoading: Ref<boolean> = ref(false)
  const isChangingDevice: Ref<boolean> = ref(false)

  const selectedAudioInputDeviceId: Ref<string> = ref('')
  const selectedAudioOutputDeviceId: Ref<string> = ref('')
  const outputSelectionSupported: Ref<boolean> = ref('setSinkId' in new AudioContext())

  const recordingStream = ref<MediaStream | null>(null)
  let activeRecordingRequestId = 0
  const IPAD_MIC_LABEL_HINTS = ['ipad microphone']
  const PREFERRED_DEVICE_LABEL_HINTS = ['best audio']

  const isIpadMicrophoneLabel = (label?: string | null) => {
    if (!label) return false
    const lowerLabel = label.toLowerCase()
    return IPAD_MIC_LABEL_HINTS.some((hint) => lowerLabel.includes(hint))
  }

  const isPreferredDeviceLabel = (label?: string | null) => {
    if (!label) return false
    const lowerLabel = label.toLowerCase()
    return PREFERRED_DEVICE_LABEL_HINTS.some((hint) => lowerLabel.includes(hint))
  }

  const isIpadMicrophoneDevice = (device?: DeviceInfo | null) => {
    if (!device) return false
    return isIpadMicrophoneLabel(device.label)
  }

  const getPreferredInputDevices = () =>
    audioDevices.value.filter(
      (device) => device.kind === 'audioinput' && isPreferredDeviceLabel(device.label)
    )

  function hasPreferredInputDeviceAvailable() {
    return getPreferredInputDevices().length > 0
  }

  function isPreferredInputDeviceSelected() {
    const currentDevice = getCurrentDeviceInfo('input')
    if (!currentDevice) return false
    return isPreferredDeviceLabel(currentDevice.label)
  }

  function getPreferredInputDeviceSummaries() {
    return getPreferredInputDevices().map((device) => ({
      label: device.label,
      deviceId: device.deviceId,
      groupId: device.groupId ?? null
    }))
  }

  const isDeviceSelectionLocked = () => dataStore.screenerInProgress === true

  const microphonePermissionRequired: Ref<boolean> = ref(true)
  const microphonePermission: Ref<boolean> = ref(false)
  const microphonePermissionState: Ref<string> = ref('')
  const browserType: Ref<string> = ref(deviceStore.getBrowserType())

  type LogLevel = 'info' | 'warn' | 'error'

  const extractDeviceListForLog = (
    devices: DeviceInfo[]
  ): Array<Pick<DeviceInfo, 'deviceId' | 'label' | 'kind' | 'groupId'>> => {
    if (!devices?.length) return []
    return devices.map((device) => ({
      deviceId: device.deviceId,
      label: device.label,
      kind: device.kind,
      groupId: device.groupId ?? null
    }))
  }

  const summarizeSelectedDevice = (
    deviceId: string,
    kind: 'audioinput' | 'audiooutput'
  ): { deviceId: string; label: string | null; groupId: string | null; kind: string } => {
    const normalizedId = deviceId || 'default'
    const device =
      audioDevices.value.find(
        (currentDevice) => currentDevice.deviceId === deviceId && currentDevice.kind === kind
      ) || null

    return {
      deviceId: normalizedId,
      label: device?.label || (deviceId ? 'unknown' : 'default'),
      groupId: device?.groupId ?? null,
      kind
    }
  }

  /**
   * Sets up the screener media.
   */

  async function setupScreenerMedia() {
    logger.info('Setting up screener media')

    if (dataStore.screenerData?.metadata?.mediaSettings?.requiresRecording === false) {
      microphonePermissionRequired.value = false
      requiresAudioDevicePrompt.value = false
      return
    }

    await checkForMicrophonePermissions()
  }

  /**
   * Checks for microphone permissions and sets the permissions state accordingly.
   */
  async function checkForMicrophonePermissions() {
    logger.info('Checking for microphone permissions')

    const permissions = await navigator?.permissions?.query({
      name: 'microphone' as PermissionName
    })

    if (permissions !== undefined) {
      permissions.onchange = async () => {
        await setPermissionsState(permissions.state)
      }
    }

    await mediaDevicesApi
      .getUserMedia({ audio: true })
      .then(async () => {
        await setPermissionsState('granted').then(() => {
          return true
        })
      })
      .catch(async (error) => {
        logger.error('Error getting microphone permissions', error)
        await setPermissionsState('denied')
        setRequiredAudioDevicePrompt(true, true)
      })
  }

  /**
   * Sets the permissions state for the media devices.
   * @param state - The state of the permissions.
   */
  async function setPermissionsState(state: string): Promise<void> {
    if (state === microphonePermissionState.value) return

    logger.info('Setting mic permissions state', state)
    microphonePermission.value = state === 'granted'
    microphonePermissionState.value = state

    if (microphonePermission.value) {
      await getDevices()
    }
  }

  /**
   * Retrieves the list of audio devices.
   */
  async function getDevices() {
    getStoredAudioDevices()
    audioDevices.value = await getAudioDevicesList()

    await ensurePreferredAudioInputDevice()
    const promptRequired = await checkIfPromptRequired()

    if (promptRequired) {
      await ensurePreferredAudioInputDevice(true)
    }

    await setAudioRecordingStream()
  }

  async function logTechTestSummary() {
    const summary = {
      deviceUUID: localStorage.getItem('mable_device_uuid') || 'unknown',
      selectedInputDevice: summarizeSelectedDevice(selectedAudioInputDeviceId.value, 'audioinput'),
      selectedOutputDevice: summarizeSelectedDevice(
        selectedAudioOutputDeviceId.value,
        'audiooutput'
      ),
      availableDevices: extractDeviceListForLog(audioDevices.value),
      browserType: browserType.value,
      isIpad,
      isSafari
    }

    try {
      await activeScreenerStore.logToDatabase('tech_test_summary', summary)
    } catch (error) {
      logger.error('Failed to log tech test summary', error)
    }
  }

  /**
   * Retrieves the list of audio devices.
   */
  async function getAudioDevicesList(): Promise<DeviceInfo[]> {
    logger.info('Getting audio devices list')
    mediaDevicesLoading.value = true
    const devices = await mediaDevicesApi.enumerateDevices()

    mediaDevicesLoading.value = false
    return (devices as DeviceInfo[]) || []
  }

  /**
   * Checks if the selected device ID is available.
   * @param selectedDeviceId - The selected device ID.
   * @param deviceKind - The kind of the device.
   */
  async function checkMediaDeviceAvailability(selectedDeviceId: string, deviceKind: string) {
    if (!audioDevices.value.length) {
      audioDevices.value = await getAudioDevicesList()
    }

    const deviceExists = audioDevices.value.find(
      (device) => device.deviceId === selectedDeviceId && device.kind === deviceKind
    )

    if (!selectedDeviceId || !deviceExists) {
      if (isDeviceSelectionLocked()) {
        logger.warn(
          `Device ${selectedDeviceId || 'unknown'} (${deviceKind}) missing but screener is active; keeping previous selection`
        )
      } else {
        setAudioDeviceId(deviceKind, '') // Clear the device id only when safe
      }
      return false
    }
    return true
  }

  /**
   * Checks if stored audio devices are available
   */
  async function checkIfPromptRequired(): Promise<boolean> {
    logger.info('Checking if prompt is required')

    const inputSet = await checkMediaDeviceAvailability(
      selectedAudioInputDeviceId.value,
      'audioinput'
    )
    logger.info(`Stored input device is ${inputSet ? '' : 'not '}available`)

    const outputSet =
      !outputSelectionSupported.value ||
      (await checkMediaDeviceAvailability(selectedAudioOutputDeviceId.value, 'audiooutput'))
    logger.info(`Stored output device is ${outputSet ? '' : 'not '}available`)

    const selectedInputDevice = audioDevices.value.find(
      (device) => device.deviceId === selectedAudioInputDeviceId.value
    )
    const selectedOutputDevice = audioDevices.value.find(
      (device) => device.deviceId === selectedAudioOutputDeviceId.value
    )
    const usingIpadMic =
      isIpadMicrophoneDevice(selectedInputDevice) || isIpadMicrophoneDevice(selectedOutputDevice)

    // Check if prompt is required
    const requiresPrompt = usingIpadMic || !outputSet || !inputSet

    logger.info(`Prompt is ${requiresPrompt ? '' : 'not '}required`)
    setRequiredAudioDevicePrompt(requiresPrompt, true)
    return requiresPrompt
  }

  /**
   * Ensures a sensible default audio input device is selected, preferring non-iPad mics when possible.
   * @param force - When true, override any existing selection (e.g. after device list changes).
   */
  async function ensurePreferredAudioInputDevice(force: boolean = false) {
    if (isDeviceSelectionLocked()) {
      logger.info('Screener in progress; skipping automatic audio input preference updates')
      return
    }

    const preferredDevicesApplied = await applyPreferredDevicePair(force)
    if (preferredDevicesApplied) {
      logger.info('Preferred headset devices applied; skipping standard input preference logic', {
        force
      })
      return
    }

    const audioInputDevices = audioDevices.value.filter((device) => device.kind === 'audioinput')
    if (!audioInputDevices.length) {
      logger.info('No audio input devices available when attempting to ensure preferred mic')
      return
    }

    // Determine the preferred device using our priority rules.
    const findDeviceById = (devices: DeviceInfo[], id: string) =>
      devices.find((device) => device.deviceId === id)
    const findDeviceByGroupId = (devices: DeviceInfo[], id: string) =>
      devices.find((device) => device.groupId === id)
    const findDefaultDevice = (devices: DeviceInfo[]) => findDeviceById(devices, 'default')

    const selectedOutputDevice = findDeviceById(
      audioDevices.value,
      selectedAudioOutputDeviceId.value
    )
    const groupMatchDevice = selectedOutputDevice?.groupId
      ? findDeviceByGroupId(audioInputDevices, selectedOutputDevice.groupId)
      : null

    const nonIpadMics = audioInputDevices.filter((device) => !isIpadMicrophoneDevice(device))

    const preferredDevice =
      groupMatchDevice || // Priority 1: Headset mic (matches output group)
      findDefaultDevice(nonIpadMics) || // Priority 2: Default mic, if not iPad
      nonIpadMics[0] || // Priority 3: Any other non-iPad mic
      findDefaultDevice(audioInputDevices) || // Priority 4: Default mic, even if iPad
      audioInputDevices[0] // Priority 5: Fallback to the first mic in the list

    if (!preferredDevice?.deviceId) {
      logger.info('Unable to determine a preferred audio input device')
      return
    }

    // Decide whether we can keep the current selection.
    const preferredDeviceId = preferredDevice.deviceId
    const currentDeviceId = selectedAudioInputDeviceId.value
    const storedDeviceId = sessionStorage.getItem(INPUT_DEVICE_ID_KEY)

    const currentDevice = findDeviceById(audioInputDevices, currentDeviceId)
    const currentLooksLikeIpadMic = isIpadMicrophoneDevice(currentDevice)

    const isAlreadyPreferred = currentDeviceId === preferredDeviceId
    const isStoredSelection = storedDeviceId === currentDeviceId

    // Keep the existing device when it is not the iPad mic and matches either the preferred choice or the stored user preference.
    if (!force && !currentLooksLikeIpadMic && (isAlreadyPreferred || isStoredSelection)) {
      logger.info('No change needed for audio input device', {
        currentDeviceId,
        preferredDeviceId,
        isStoredSelection
      })
      return
    }

    // Otherwise, switch to the preferred device.
    logger.info('Auto-selecting preferred audio input device', {
      preferredDevice,
      currentDevice,
      force
    })

    try {
      await changeInputDevice(preferredDeviceId, true)
      if (preferredDeviceId) {
        storeAudioDeviceId('audioinput', preferredDeviceId)
      }
    } catch (error) {
      logger.error('Failed to auto-select preferred audio input device', {
        error,
        preferredDeviceId
      })
    }
  }

  async function applyPreferredDevicePair(force: boolean = false): Promise<boolean> {
    if (isDeviceSelectionLocked()) {
      logger.info('Screener in progress; skipping preferred device pair enforcement')
      return false
    }

    if (!audioDevices.value.length) return false

    const preferredInput = audioDevices.value.find(
      (device) => device.kind === 'audioinput' && isPreferredDeviceLabel(device.label)
    )
    const preferredOutput = audioDevices.value.find(
      (device) => device.kind === 'audiooutput' && isPreferredDeviceLabel(device.label)
    )

    if (!preferredInput || !preferredOutput) {
      return false
    }

    const inputDeviceId = preferredInput.deviceId ?? ''
    const outputDeviceId = preferredOutput.deviceId ?? ''

    const inputMatches = selectedAudioInputDeviceId.value === inputDeviceId
    const outputMatches = selectedAudioOutputDeviceId.value === outputDeviceId
    const streamActive = recordingStream.value !== null

    if (!force && inputMatches && outputMatches && streamActive) {
      logger.info('Preferred headset devices already active; reinforcing stored preferences', {
        inputDeviceId,
        outputDeviceId
      })
      storeAudioDeviceId('audioinput', inputDeviceId)
      storeAudioDeviceId('audiooutput', outputDeviceId)
      return true
    }

    logger.info('Preferred headset devices detected, applying as defaults', {
      inputDeviceId,
      outputDeviceId,
      force
    })

    try {
      await changeInputDevice(inputDeviceId, true)
      storeAudioDeviceId('audioinput', inputDeviceId)
      setAudioDeviceId('audiooutput', outputDeviceId)
      storeAudioDeviceId('audiooutput', outputDeviceId)
    } catch (error) {
      logger.error('Failed to apply preferred headset microphone selection', {
        error,
        inputDeviceId
      })
      return false
    }

    return true
  }

  /**
   * Sets up a new recording stream with the specified audio input device ID.
   * Returns true when this call created or now owns an active stream, false when superseded by a newer request.
   */
  async function setAudioRecordingStream(
    targetDeviceId: string = selectedAudioInputDeviceId.value
  ): Promise<boolean> {
    if (recordingStream.value !== null) return true
    logger.info('Setting up new recording stream with audio input device ID: ', targetDeviceId)
    const selectedDeviceId = targetDeviceId
    const audioConstraints: MediaTrackConstraints = {
      echoCancellation: true
    }
    if (selectedDeviceId) {
      audioConstraints.deviceId =
        selectedDeviceId === 'default' ? { ideal: selectedDeviceId } : { exact: selectedDeviceId }
    }

    const constraints: MediaStreamConstraints = {
      audio: audioConstraints,
      video: false
    }
    try {
      const requestId = ++activeRecordingRequestId
      const stream = await mediaDevicesApi.getUserMedia(constraints)

      if (requestId !== activeRecordingRequestId) {
        logger.info('Discarding stale recording stream request', {
          requestId,
          activeRequestId: activeRecordingRequestId,
          selectedDeviceId
        })
        stream.getTracks().forEach((track) => track.stop())
        return false
      }

      recordingStream.value = stream
      logger.info('Recording stream set up', { deviceId: selectedDeviceId })

      activeScreenerStore.updateMediaDeviceOnSentryData()
      return true
    } catch (error) {
      logger.error('Error setting up recording stream')
      throw error
    }
  }

  /**
   * Closes the recording stream.
   */
  function closeRecordingStream(): void {
    activeRecordingRequestId++
    if (recordingStream.value === null) return
    logger.info('Closing recording stream', recordingStream.value)

    recordingStream.value?.getTracks().forEach((track) => {
      track.stop()
    })

    recordingStream.value = null
  }

  /**
   * Retrieves the stored audio devices from session storage.
   */
  function getStoredAudioDevices(): { audioInputId: string; audioOutputId: string } {
    logger.info('Getting stored audio devices')

    selectedAudioInputDeviceId.value = sessionStorage.getItem(INPUT_DEVICE_ID_KEY) || ''
    selectedAudioOutputDeviceId.value = sessionStorage.getItem(OUTPUT_DEVICE_ID_KEY) || ''
    logger.info(
      `Stored audio input device ID: '${selectedAudioInputDeviceId.value}', stored audio output device ID: '${selectedAudioOutputDeviceId.value}'`
    )
    return {
      audioInputId: selectedAudioInputDeviceId.value,
      audioOutputId: selectedAudioOutputDeviceId.value
    }
  }

  /**
   * Sets the audio device ID of the specified type.
   * @param type - The type of the audio device.
   * @param deviceId - The ID of the audio device.
   */
  function setAudioDeviceId(type: string, deviceId: string) {
    logger.info(`Setting audio device ID of type: '${type}' and ID: '${deviceId}'`)

    if (type === 'audioinput') {
      selectedAudioInputDeviceId.value = deviceId
    } else if (type === 'audiooutput') {
      selectedAudioOutputDeviceId.value = deviceId
    }
  }

  /**
   * Stores the audio device ID of the specified type.
   * @param type - The type of the audio device.
   * @param deviceId - The ID of the audio device.
   */
  function storeAudioDeviceId(type: string, deviceId: string) {
    logger.info(`Storing audio device ID of type: '${type}' and ID: '${deviceId}'`)
    if (type === 'audioinput') {
      sessionStorage.setItem(INPUT_DEVICE_ID_KEY, deviceId)
    } else if (type === 'audiooutput') {
      // Store the selected audio output device ID in session storage
      sessionStorage.setItem(OUTPUT_DEVICE_ID_KEY, deviceId)
    }
  }

  /**
   * Changes the input device to the specified device ID.
   * @param deviceId - The ID of the device.
   */
  async function changeInputDevice(deviceId: string | null, force: boolean = false) {
    const nextDeviceId = deviceId ?? ''
    const currentDeviceId = selectedAudioInputDeviceId.value

    const isDefaultSelection = nextDeviceId === '' || nextDeviceId === 'default'

    if (!force && nextDeviceId === currentDeviceId && !isDefaultSelection) {
      logger.info('Selected audio input device unchanged, skipping update', nextDeviceId)
      return
    }

    logger.info(`Changing input device to: ${nextDeviceId}`, { force, currentDeviceId })

    const originalDeviceId = currentDeviceId
    setAudioDeviceId('audioinput', nextDeviceId)

    try {
      closeRecordingStream()
      const streamCreated = await setAudioRecordingStream(nextDeviceId)

      if (!streamCreated) {
        logger.info('Recording stream request superseded by a newer request', {
          nextDeviceId
        })
        return
      }

      if (!recordingStream.value) {
        throw new Error('setAudioRecordingStream did not create a stream.')
      }
    } catch (error) {
      logger.error('Failed to update recording stream, reverting device selection', error)
      setAudioDeviceId('audioinput', originalDeviceId)

      closeRecordingStream() // ensure any partial/failed stream is closed
      await setAudioRecordingStream(originalDeviceId).catch((restoreError) => {
        logger.error('Failed to restore original audio stream', restoreError)
      })

      throw error
    }
  }

  /**
   * Sets the required audio device prompt.
   */
  function setRequiredAudioDevicePrompt(value: boolean, forced: boolean = true) {
    logger.info('Setting required audio device prompt', value)
    requiresAudioDevicePrompt.value = value
    requiresAudioDevicePromptForced.value = forced
  }

  function getCurrentDeviceInfo(type: 'input' | 'output'): DeviceInfo | null {
    const selectedId =
      type === 'input' ? selectedAudioInputDeviceId.value : selectedAudioOutputDeviceId.value
    const device = audioDevices.value.find((d) => d.deviceId === selectedId)
    if (!device) return null

    return {
      deviceId: device.deviceId!,
      kind: device.kind!,
      label: device.label,
      groupId: device.groupId ?? null
    }
  }

  /**
   * Listens for device changes and updates the audio device list accordingly.
   * Timeout is implemented to prevent multiple updates in quick succession especially on IOS devices where bluetooth devices can cause multiple device change events.
   */
  function listenForDeviceChanges() {
    if (!mediaDevicesApi) return
    logger.info('Listening for device changes', mediaDevicesApi)

    mediaDevicesApi.ondevicechange = () => {
      clearManagedTimeout(deviceChangeTimeoutId)

      deviceChangeTimeoutId.value = setManagedTimeout(async () => {
        const oldList = [...audioDevices.value]
        const newList = await getAudioDevicesList()
        const deviceListChange = JSON.stringify(oldList) !== JSON.stringify(newList)

        if (!deviceListChange) return
        logger.info('Device list change detected')
        audioDevices.value = newList
        if (dataStore.screenerInProgress) {
          dataStore.setScreenerError(
            'AudioDeviceError',
            'We lost the approved microphone. Please reconnect it and refresh the page before continuing.'
          )
        }
        await ensurePreferredAudioInputDevice(true)
        await checkMediaDeviceAvailability(selectedAudioOutputDeviceId.value, 'audiooutput')
      }, 1000)
    }
  }

  listenForDeviceChanges()

  return {
    selectedAudioInputDeviceId,
    selectedAudioOutputDeviceId,
    microphonePermission,
    browserType,
    isIpad,
    isSafari,
    browserTypes,
    recordingStream,
    audioDevices,
    outputSelectionSupported,
    requiresAudioDevicePrompt,
    microphonePermissionRequired,
    requiresAudioDevicePromptForced,

    setupScreenerMedia,
    checkForMicrophonePermissions,
    getStoredAudioDevices,
    checkMediaDeviceAvailability,
    closeRecordingStream,
    changeInputDevice,
    storeAudioDeviceId,
    setRequiredAudioDevicePrompt,
    setAudioRecordingStream,
    getCurrentDeviceInfo,
    hasPreferredInputDeviceAvailable,
    isPreferredInputDeviceSelected,
    getPreferredInputDeviceSummaries,
    logTechTestSummary
  }
})
